import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Play, Pause } from 'lucide-react';
import { AnimatedText, SlideUpText, GoldUnderline } from './TextAnimations';
import { useLanguage } from '../context/LanguageContext';

const VideoShowcase = () => {
  const [isPlaying, setIsPlaying] = useState(true);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { t, language } = useLanguage();

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const descriptions = {
    en: {
      p1: 'Every project begins with a vision and ends with a transformed space. Our process combines technical precision with meticulous attention to detail.',
      p2: 'From strategic demolition to luxury finishes, we supervise every phase with the commitment to exceed expectations.',
      caption1: 'In Progress',
      caption2: 'Residential Renovation',
    },
    es: {
      p1: 'Cada proyecto comienza con una visión y termina con un espacio transformado. Nuestro proceso combina precisión técnica con atención meticulosa al detalle.',
      p2: 'Desde la demolición estratégica hasta los acabados de lujo, supervisamos cada fase con el compromiso de superar expectativas.',
      caption1: 'En Proceso',
      caption2: 'Renovación Residencial',
    },
  };

  return (
    <section ref={sectionRef} className="py-32 bg-[#030303] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#C5A55C]/20 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <motion.span 
              className="font-montserrat font-light text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              {t('video.tagline')}
            </motion.span>
            
            <h2 className="font-montserrat font-extralight text-4xl md:text-5xl text-white tracking-[0.08em] uppercase mb-8 leading-tight">
              <AnimatedText text={t('video.title1')} delay={0.3} />
              <br />
              <span className="text-[#C5A55C]">
                <AnimatedText text={t('video.title2')} delay={0.6} />
              </span>
            </h2>

            <div className="w-16 mb-8">
              <GoldUnderline delay={0.9} />
            </div>
            
            <SlideUpText delay={0.8}>
              <p className="font-raleway font-light text-base text-white/50 leading-relaxed tracking-wide mb-6">
                {descriptions[language].p1}
              </p>
              
              <p className="font-raleway font-light text-sm text-white/30 leading-relaxed tracking-wide">
                {descriptions[language].p2}
              </p>
            </SlideUpText>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-12 flex gap-12"
            >
              <div>
                <div className="font-montserrat font-extralight text-3xl text-[#C5A55C]">98%</div>
                <div className="font-raleway font-light text-xs text-white/30 tracking-wide mt-1">{t('video.stat1')}</div>
              </div>
              <div>
                <div className="font-montserrat font-extralight text-3xl text-[#C5A55C]">4.9</div>
                <div className="font-raleway font-light text-xs text-white/30 tracking-wide mt-1">{t('video.stat2')}</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Video */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            className="relative"
          >
            <div className="relative aspect-[4/3] overflow-hidden group">
              {/* Video */}
              <video
                ref={videoRef}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-cover"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_734cb631-c213-4223-9ec5-3c4d6675302f/artifacts/370ru7pm_7376-199627588_small.mp4" 
                  type="video/mp4" 
                />
              </video>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-all duration-500" />
              
              {/* Gold border on hover */}
              <div className="absolute inset-0 border border-transparent group-hover:border-[#C5A55C]/30 transition-all duration-500" />
              
              {/* Play/Pause Button */}
              <motion.button
                onClick={togglePlay}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="absolute bottom-6 right-6 w-14 h-14 border border-white/30 hover:border-[#C5A55C] hover:bg-[#C5A55C] flex items-center justify-center transition-all duration-300 group/btn"
                data-hover
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-white group-hover/btn:text-black transition-colors" />
                ) : (
                  <Play className="w-5 h-5 text-white group-hover/btn:text-black transition-colors ml-1" />
                )}
              </motion.button>

              {/* Caption */}
              <div className="absolute bottom-6 left-6">
                <span className="font-montserrat font-light text-[10px] tracking-[0.25em] text-[#C5A55C] uppercase block mb-1">
                  {descriptions[language].caption1}
                </span>
                <span className="font-raleway font-light text-sm text-white/70">
                  {descriptions[language].caption2}
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VideoShowcase;
